import React, { useMemo } from 'react';
import { Check, Flame } from 'lucide-react';

// Prospect Card Component
function ProspectCard({ prospect, onSelectProspect, getPriorityBorder, onToggleHot }) {
  const hasRevenue = (prospect.revenuePotential || 0) > 0;

  return (
    <div
      onClick={() => onSelectProspect(prospect)}
      className={`${getPriorityBorder(prospect.priority)} bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-pointer border border-[#e8eaf2] ${
        prospect.isHot ? 'ring-1 ring-orange-200' : ''
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-[#011745] text-sm line-clamp-2">
            {prospect.isHot && <span className="text-orange-500 mr-1 text-xs">🔥</span>}
            {prospect.company}
          </h4>
          <p className="text-xs text-[#7b859e] mt-1">{prospect.contactName}</p>
        </div>
        {onToggleHot && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleHot(prospect.id);
            }}
            disabled={!prospect.isHot && !hasRevenue}
            title={prospect.isHot ? 'Hot prospect — klik om te verwijderen' : (hasRevenue ? 'Markeer als hot prospect' : 'Stel eerst FX of TF revenue in')}
            className={`flex-shrink-0 p-1 rounded transition-all ${
              prospect.isHot
                ? 'text-orange-500 bg-orange-50 hover:bg-orange-100'
                : hasRevenue
                  ? 'text-[#cdd1e0] hover:text-orange-400 hover:bg-orange-50'
                  : 'text-[#e8eaf2] cursor-not-allowed'
            }`}
          >
            <Flame size={15} fill={prospect.isHot ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>

      {/* Service Badges */}
      <div className="flex gap-2 mb-3">
        {prospect.taperPayActive && (
          <div className="flex items-center gap-1 bg-[#eef2fa] px-2 py-1 rounded text-xs">
            <Check size={12} className="text-[#3d61a4]" />
            <span className="text-[#3d61a4] font-medium">FX</span>
          </div>
        )}
        {prospect.taperTradeActive && (
          <div className="flex items-center gap-1 bg-[#e8f5e9] px-2 py-1 rounded text-xs">
            <Check size={12} className="text-[#4caf50]" />
            <span className="text-[#4caf50] font-medium">TF</span>
          </div>
        )}
        {!prospect.taperPayActive && !prospect.taperTradeActive && (
          <div className="text-xs text-[#a4abbe]">No services activated</div>
        )}
      </div>

      {/* Volumes */}
      <div className="space-y-1 mb-3 border-t border-[#e8eaf2] pt-3">
        {prospect.fxVolume > 0 && (
          <p className="text-xs text-[#7b859e]">
            FX Vol:{' '}
            <span className="font-medium text-[#011745]">
              {(prospect.fxVolume / 1000000).toFixed(1)}M
            </span>
          </p>
        )}
        {prospect.tfVolume > 0 && (
          <p className="text-xs text-[#7b859e]">
            TF Vol:{' '}
            <span className="font-medium text-[#011745]">
              {(prospect.tfVolume / 1000000).toFixed(1)}M
            </span>
          </p>
        )}
      </div>

      {/* Score */}
      <div className="flex items-center justify-between pt-2 border-t border-[#e8eaf2]">
        <span className="text-xs font-medium text-[#566079]">Score</span>
        <div className="flex items-center gap-1">
          <div className="h-2 w-16 bg-[#e8eaf2] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#3d61a4] transition-all"
              style={{ width: `${prospect.score}%` }}
            ></div>
          </div>
          <span className="text-xs font-semibold text-[#011745]">
            {prospect.score}
          </span>
        </div>
      </div>
    </div>
  );
}

// Column Component
function KanbanColumn({
  title,
  headerBgClass,
  headerTextClass,
  prospects,
  onSelectProspect,
  getPriorityBorder,
  onToggleHot,
}) {
  return (
    <div className="flex-shrink-0 w-80">
      {/* Column Header */}
      <div className={`${headerBgClass} rounded-lg p-4 mb-4 flex items-center justify-between`}>
        <h3 className={`font-semibold ${headerTextClass}`}>
          {title}
        </h3>
        <span className="text-sm font-medium text-[#7b859e]">
          {prospects.length}
        </span>
      </div>

      {/* Cards Container */}
      <div className="space-y-3 pb-4 min-h-96">
        {prospects.length === 0 ? (
          <div className="text-center py-8 text-[#a4abbe]">
            <p className="text-sm">No prospects</p>
          </div>
        ) : (
          prospects.map((prospect) => (
            <ProspectCard
              key={prospect.id}
              prospect={prospect}
              onSelectProspect={onSelectProspect}
              getPriorityBorder={getPriorityBorder}
              onToggleHot={onToggleHot}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function ProspectKanban({
  prospects,
  onSelectProspect,
  onToggleHot,
}) {
  const columns = useMemo(() => ({
    hot: prospects.filter((p) => p.isHot),
    rest: prospects.filter((p) => !p.isHot),
  }), [prospects]);

  const getPriorityBorder = (priority) => {
    const colors = {
      Critical: 'border-l-4 border-red-500',
      High: 'border-l-4 border-orange-500',
      Medium: 'border-l-4 border-blue-500',
      Low: 'border-l-4 border-gray-400',
    };
    return colors[priority] || 'border-l-4 border-gray-300';
  };

  return (
    <div className="p-6 h-full overflow-x-auto bg-[#f7f8fc]">
      <div className="flex gap-6 min-w-full">
        <KanbanColumn
          title="🔥 Hot Prospects"
          headerBgClass="bg-[#fff4e6]"
          headerTextClass="text-[#ff9800]"
          prospects={columns.hot}
          onSelectProspect={onSelectProspect}
          getPriorityBorder={getPriorityBorder}
          onToggleHot={onToggleHot}
        />
        <KanbanColumn
          title="Prospects"
          headerBgClass="bg-[#eef2fa]"
          headerTextClass="text-[#3d61a4]"
          prospects={columns.rest}
          onSelectProspect={onSelectProspect}
          getPriorityBorder={getPriorityBorder}
          onToggleHot={onToggleHot}
        />
      </div>
    </div>
  );
}
